import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  FolderOpen,
  Search,
  AlertTriangle,
  Calendar,
  ArrowUp,
  ArrowDown,
  User,
  Stethoscope,
  FileText,
  ChevronRight,
  Archive,
  Trash2,
  UserPlus,
  Loader2,
  Printer,
  LayoutGrid,
  List,
  Camera,
  CreditCard,
  XCircle,
  Plus,
  CheckCircle2,
  ClipboardList,
  Users,
  ExternalLink,
  Bed,
  Sun,
  Clock,
  MessageCircle,
  LogOut,
  Send,
  Paperclip,
  FileX,
} from "lucide-react";
import {
  resolveStatoOspite,
  ospiteStatoLabel,
  ospiteStatoDot,
  ospiteStatoVisuals,
  formatDataItShort,
  matchOspiteStatoSearch,
  STATO_ATTIVO,
  STATO_PRE_INGRESSO,
  STATO_DIMISSIONE_PROGRAMMATA,
  STATO_DIMESSO,
  OSPITE_STATO_FILTERS,
} from "../utils/ospiteStato";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { Textarea } from "../components/ui/textarea";
import PageHeader from "../components/PageHeader";
import { formatCameraAssegnata } from "../utils/formatCamera";
import RicercaOspiteGlobaleDialog, { RicercaOspiteGlobaleButton } from "../components/RicercaOspiteGlobaleDialog";
import DimissioniRapideDialog from "../components/fascicoli/DimissioniRapideDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function resizeImageFileToJpegDataUrl(file, maxEdge = 720, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let w = img.naturalWidth;
      let h = img.naturalHeight;
      if (w > maxEdge || h > maxEdge) {
        if (w >= h) {
          h = Math.round((h * maxEdge) / w);
          w = maxEdge;
        } else {
          w = Math.round((w * maxEdge) / h);
          h = maxEdge;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("canvas"));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("load"));
    };
    img.src = url;
  });
}

function escapeHtml(text) {
  if (text == null || text === undefined) return "";
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatDataNascitaLista(s) {
  if (!s || !String(s).trim()) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleDateString("it-IT");
}

function formatDataUscitaBadge(s) {
  if (!s || !String(s).trim()) return "";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? String(s).trim() : d.toLocaleDateString("it-IT");
}

/** La lista API espande l'anagrafica solo da backend recente; senza questi campi serve il GET dettaglio. */
function listaFascicoliHaCampiStampa(sample) {
  return (
    sample != null &&
    Object.prototype.hasOwnProperty.call(sample, "codice_fiscale")
  );
}

function formatContattiFamiliariStampa(contatti) {
  if (!Array.isArray(contatti) || contatti.length === 0) return "";
  return contatti
    .map((cf) => {
      const nome = String(cf?.nome ?? "").trim();
      const rel = String(cf?.relazione ?? "").trim();
      const tel = String(cf?.telefono ?? "").trim();
      const em = String(cf?.email ?? "").trim();
      const head = [nome, rel ? `(${rel})` : ""].filter(Boolean).join(" ");
      const tail = [tel, em].filter(Boolean).join(" · ");
      return [head, tail].filter(Boolean).join(" — ");
    })
    .filter(Boolean)
    .join("; ");
}

function formatValoriContattiFamiliari(contatti, campo) {
  if (!Array.isArray(contatti) || contatti.length === 0) return "";
  return contatti
    .map((cf) => String(cf?.[campo] ?? "").trim())
    .filter(Boolean)
    .join("; ");
}

/** Unisce anagrafica da dettaglio (se caricata) con item lista e fallback su nome_completo. */
function buildRigaStampa(c, fascicoloDettaglio) {
  const a = fascicoloDettaglio?.anagrafica || {};
  const doc = fascicoloDettaglio?.documento || {};
  const contatti = a.contatti_familiari || [];
  let nome = String(a.nome ?? c.nome ?? "").trim();
  let cognome = String(a.cognome ?? c.cognome ?? "").trim();
  if (!nome && !cognome) {
    const parts = String(c.nome_completo || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length === 1) {
      nome = parts[0];
    } else if (parts.length > 1) {
      nome = parts[0];
      cognome = parts.slice(1).join(" ");
    }
  }
  const dataNascita = String(a.data_nascita ?? c.data_nascita ?? "").trim();
  let luogo = String(a.luogo_nascita ?? c.luogo_nascita ?? "").trim();
  if (!luogo) luogo = String(a.comune_nascita_label ?? "").trim();
  const cf = String(a.codice_fiscale ?? c.codice_fiscale ?? "").trim();
  const sesso = String(a.sesso ?? c.sesso ?? "").trim();
  const amm = fascicoloDettaglio?.amministrativa || {};
  const rettaRaw = amm.retta_mensile ?? c.retta_mensile ?? null;
  const rettaMensile =
    rettaRaw != null && rettaRaw !== "" && Number.isFinite(Number(rettaRaw))
      ? Number(rettaRaw)
      : null;
  const cameraRaw = String(c.camera ?? "").trim();
  const statoOspite = resolveStatoOspite(
    fascicoloDettaglio
      ? {
          ...fascicoloDettaglio,
          stato_ospite: fascicoloDettaglio.stato_ospite ?? c.stato_ospite,
          amministrativa: fascicoloDettaglio.amministrativa || {
            data_uscita: c.data_uscita,
          },
        }
      : c
  );
  return {
    nome,
    cognome,
    data_nascita: dataNascita,
    luogo_nascita: luogo,
    codice_fiscale: cf,
    sesso,
    indirizzo_residenza: String(a.indirizzo_residenza ?? "").trim(),
    telefono: String(a.telefono ?? "").trim(),
    email_ospite: String(a.email ?? "").trim(),
    numero_documento: String(a.numero_documento ?? doc.numero ?? "").trim(),
    tipo_documento: String(
      a.tipo_documento_label ?? a.tipo_documento ?? doc.tipo ?? ""
    ).trim(),
    cittadinanza: String(a.cittadinanza_label ?? a.cittadinanza ?? "").trim(),
    email_familiari: formatValoriContattiFamiliari(contatti, "email"),
    telefono_familiari: formatValoriContattiFamiliari(contatti, "telefono"),
    contatti_familiari: formatContattiFamiliariStampa(contatti),
    camera: cameraRaw ? formatCameraAssegnata(cameraRaw) : "",
    data_ingresso: String(c.data_ingresso ?? "").trim(),
    data_uscita: String(c.data_uscita ?? "").trim(),
    diagnosi_principale: String(c.diagnosi_principale ?? "").trim(),
    medico_curante: String(c.medico_curante ?? "").trim(),
    pai_scadenza: String(c.pai_scadenza ?? "").trim(),
    tipo_ospite: c.tipo_ospite || "residente",
    stato_ospite: statoOspite,
    residente_in_struttura: c.residente_in_struttura === true,
    retta_mensile: rettaMensile,
  };
}

const STAMPA_LISTA_CAMPI = [
  { id: "nome", label: "Nome", default: true },
  { id: "cognome", label: "Cognome", default: true },
  { id: "data_nascita", label: "Data di nascita", default: true },
  { id: "luogo_nascita", label: "Luogo di nascita", default: true },
  { id: "codice_fiscale", label: "Codice fiscale", default: true, mono: true },
  { id: "sesso", label: "Sesso", default: false },
  { id: "indirizzo_residenza", label: "Residenza", default: false },
  { id: "telefono", label: "Telefono ospite", default: false },
  { id: "email_ospite", label: "Email ospite", default: false },
  { id: "numero_documento", label: "N. documento identità", default: false, mono: true },
  { id: "tipo_documento", label: "Tipo documento", default: false },
  { id: "cittadinanza", label: "Cittadinanza", default: false },
  { id: "email_familiari", label: "Email familiari", default: false },
  { id: "telefono_familiari", label: "Telefono familiari", default: false },
  { id: "contatti_familiari", label: "Contatti familiari", default: false },
  { id: "camera", label: "Camera", default: false },
  { id: "data_ingresso", label: "Data ingresso", default: false },
  { id: "retta_mensile", label: "Retta mensile", default: false },
  { id: "data_uscita", label: "Data uscita", default: false },
  { id: "stato_ospite", label: "Stato ospite", default: false },
  { id: "residente_in_struttura", label: "Residenza in struttura", default: false },
  { id: "tipo_ospite", label: "Tipo ospite", default: false },
  { id: "diagnosi_principale", label: "Diagnosi principale", default: false },
  { id: "medico_curante", label: "Medico curante", default: false },
  { id: "pai_scadenza", label: "Scadenza PAI", default: false },
];

const ANAGRAFICA_STAMPA_CAMPI = new Set([
  "nome",
  "cognome",
  "data_nascita",
  "luogo_nascita",
  "codice_fiscale",
  "sesso",
]);

const STAMPA_CAMPI_SOLO_DETTAGLIO = new Set([
  "indirizzo_residenza",
  "telefono",
  "email_ospite",
  "numero_documento",
  "tipo_documento",
  "cittadinanza",
  "email_familiari",
  "telefono_familiari",
  "contatti_familiari",
]);

function createDefaultCampiStampa() {
  return Object.fromEntries(STAMPA_LISTA_CAMPI.map((c) => [c.id, c.default]));
}

function formatSessoStampa(s) {
  if (!s || !String(s).trim()) return "—";
  const v = String(s).trim().toLowerCase();
  if (v === "m" || v === "maschio") return "Maschio";
  if (v === "f" || v === "femmina") return "Femmina";
  return String(s).trim();
}

function formatTipoOspiteStampa(t) {
  if (t === "diurno") return "Diurno";
  if (t === "residente") return "Residente";
  return t ? String(t) : "—";
}

function valoreCampoStampa(campo, riga) {
  switch (campo.id) {
    case "data_nascita":
    case "data_ingresso":
    case "data_uscita":
    case "pai_scadenza":
      return formatDataNascitaLista(riga[campo.id]);
    case "sesso":
      return formatSessoStampa(riga.sesso);
    case "tipo_ospite":
      return formatTipoOspiteStampa(riga.tipo_ospite);
    case "stato_ospite":
      return ospiteStatoLabel(riga.stato_ospite) || "—";
    case "residente_in_struttura":
      return riga.residente_in_struttura ? "Sì" : "No";
    case "camera":
      return riga.camera || "—";
    case "retta_mensile": {
      const importo = riga.retta_mensile;
      if (importo == null || !Number.isFinite(importo) || importo <= 0) return "—";
      return `€ ${importo.toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    default:
      return riga[campo.id] ? String(riga[campo.id]) : "—";
  }
}

function stampaRichiedeDettaglioAnagrafica(campiIds, sample) {
  if (campiIds.some((id) => STAMPA_CAMPI_SOLO_DETTAGLIO.has(id))) return true;
  if (!campiIds.some((id) => ANAGRAFICA_STAMPA_CAMPI.has(id))) return false;
  return !listaFascicoliHaCampiStampa(sample);
}

function generaHtmlStampaLista(righe, campiIds, n) {
  const campi = STAMPA_LISTA_CAMPI.filter((c) => campiIds.includes(c.id));
  const dataStampa = new Date().toLocaleString("it-IT");
  const headerCells = campi
    .map((c) => `<th>${escapeHtml(c.label)}</th>`)
    .join("");
  const bodyRows = righe
    .map((riga) => {
      const cells = campi
        .map((c) => {
          const val = escapeHtml(valoreCampoStampa(c, riga));
          const cls = c.mono ? ' class="mono"' : "";
          return `<td${cls}>${val}</td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");
  return `<!DOCTYPE html>
<html lang="it">
<head>
  <meta charset="utf-8" />
  <title>Elenco ospiti — Fascicoli assistenziali</title>
  <style>
    body { font-family: system-ui, sans-serif; margin: 24px; color: #111; }
    h1 { font-size: 18px; margin: 0 0 8px; }
    .meta { font-size: 12px; color: #555; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th, td { border: 1px solid #ccc; padding: 8px 10px; text-align: left; vertical-align: top; }
    th { background: #f4f4f5; font-weight: 600; }
    tr:nth-child(even) { background: #fafafa; }
    .mono { font-family: ui-monospace, monospace; }
    @media print { body { margin: 12mm; } }
  </style>
</head>
<body>
  <h1>Elenco ospiti — Fascicoli assistenziali</h1>
  <p class="meta">Stampato il ${escapeHtml(dataStampa)} · ${n} ${n === 1 ? "ospite" : "ospiti"}</p>
  <table>
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
  <script>window.onload = function () { window.print(); };</script>
</body>
</html>`;
}

function apriFinestraStampa(html) {
  const w = window.open("about:blank", "_blank");
  if (w) {
    try {
      w.opener = null;
    } catch {
      /* ignore */
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    return true;
  }
  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "Anteprima stampa elenco ospiti");
  iframe.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none";
  document.body.appendChild(iframe);
  const idoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!idoc) {
    document.body.removeChild(iframe);
    return false;
  }
  idoc.open();
  idoc.write(html);
  idoc.close();
  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };
  iframe.contentWindow?.addEventListener("afterprint", cleanup, { once: true });
  setTimeout(cleanup, 120000);
  return true;
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Priorità clinica per lista fascicoli (card colorate e filtri). */
function computeClinicalStatus(c) {
  const today = startOfDay(new Date());
  let paiEnd = null;
  if (c.pai_scadenza && String(c.pai_scadenza).trim()) {
    const d = new Date(c.pai_scadenza);
    if (!Number.isNaN(d.getTime())) paiEnd = startOfDay(d);
  }
  const paiMissing = !paiEnd;
  const paiExpired = paiEnd != null && paiEnd < today;

  if (paiMissing || paiExpired) {
    let sub = "PAI mancante o non attivo";
    if (paiExpired && paiEnd) {
      const days = Math.round((today.getTime() - paiEnd.getTime()) / 86400000);
      sub = `PAI scaduto · da ${days} gg`;
    }
    return {
      tier: "critica",
      label: "Critica",
      alertLine: sub,
      alertDetail: paiExpired ? "Aggiornare il piano assistenziale" : "Definire un PAI attivo",
    };
  }

  const alerts = Number(c.alert_attivi) || 0;
  if (alerts > 0) {
    return {
      tier: "da_aggiornare",
      label: "Da aggiornare",
      alertLine:
        alerts === 1
          ? "1 elemento in scadenza (30 gg)"
          : `${alerts} elementi in scadenza (30 gg)`,
      alertDetail: "Valutazioni o documenti da aggiornare",
    };
  }

  const ingresso = c.data_ingresso ? startOfDay(new Date(c.data_ingresso)) : null;
  const ingressRecent =
    ingresso &&
    !Number.isNaN(ingresso.getTime()) &&
    (today.getTime() - ingresso.getTime()) / 86400000 <= 21;
  const noDiagnosi = !(c.diagnosi_principale || "").trim();
  if (ingressRecent && noDiagnosi) {
    return {
      tier: "in_valutazione",
      label: "In valutazione",
      alertLine: "Scheda clinica da completare",
      alertDetail: "Completare la prima valutazione",
    };
  }

  return {
    tier: "ok",
    label: "Tutto in regola",
    alertLine: "Documentazione aggiornata",
    alertDetail: `PAI valido fino al ${paiEnd.toLocaleDateString("it-IT")}`,
  };
}

function tierVisuals(tier) {
  switch (tier) {
    case "critica":
      return {
        border: "border-l-[5px] border-l-red-500",
        avatarBg: "bg-red-50 text-red-700 ring-red-100",
        badge: "bg-red-500 text-white gap-1 shadow-none hover:bg-red-500",
        alertBox: "bg-red-50 border-red-100 text-red-800",
        accentIcon: "text-red-600",
      };
    case "da_aggiornare":
      return {
        border: "border-l-[5px] border-l-amber-500",
        avatarBg: "bg-amber-50 text-amber-800 ring-amber-100",
        badge: "bg-amber-500 text-white gap-1 shadow-none hover:bg-amber-500",
        alertBox: "bg-amber-50 border-amber-100 text-amber-900",
        accentIcon: "text-amber-600",
      };
    case "in_valutazione":
      return {
        border: "border-l-[5px] border-l-sky-500",
        avatarBg: "bg-sky-50 text-sky-800 ring-sky-100",
        badge: "bg-sky-600 text-white gap-1 shadow-none hover:bg-sky-600",
        alertBox: "bg-sky-50 border-sky-100 text-sky-900",
        accentIcon: "text-sky-600",
      };
    default:
      return {
        border: "border-l-[5px] border-l-emerald-500",
        avatarBg: "bg-emerald-50 text-emerald-800 ring-emerald-100",
        badge: "bg-emerald-600 text-white gap-1 shadow-none hover:bg-emerald-600",
        alertBox: "bg-emerald-50 border-emerald-100 text-emerald-900",
        accentIcon: "text-emerald-600",
      };
  }
}

function computeEta(dataNascita) {
  if (!dataNascita || !String(dataNascita).trim()) return null;
  const d = new Date(dataNascita);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
}

const CHECKLIST_LEFT = [
  { key: "anagrafica", label: "Anagrafica" },
  { key: "familiari", label: "Familiari" },
  { key: "contratto", label: "Contratto" },
];

const CHECKLIST_RIGHT = [
  { key: "retta", label: "Retta" },
  { key: "email_medico", label: "Email medico" },
  { key: "pai", label: "PAI" },
];

const CHECKLIST_VOCI = [...CHECKLIST_LEFT, ...CHECKLIST_RIGHT];

const CHECKLIST_AZIONI = {
  anagrafica: "Completare anagrafica",
  familiari: "Inserire contatti familiari",
  contratto: "Firmare il contratto",
  retta: "Configurare la retta",
  email_medico: "Inserire email medico",
  pai: "Aggiornare PAI",
};

/** Dati completezza fascicolo (API o fallback locale). */
function resolveCompletezza(cartella) {
  if (cartella?.checklist && typeof cartella.completezza_percentuale === "number") {
    return {
      percent: cartella.completezza_percentuale,
      checklist: cartella.checklist,
      azioni: cartella.azioni_da_svolgere || [],
      tier:
        cartella.tier_completezza ||
        (cartella.completezza_percentuale >= 85
          ? "verde"
          : cartella.completezza_percentuale >= 60
            ? "giallo"
            : "rosso"),
    };
  }

  const today = startOfDay(new Date());
  const checklist = {};
  const anagOk =
    Boolean((cartella.nome || "").trim()) &&
    Boolean((cartella.cognome || "").trim()) &&
    Boolean((cartella.codice_fiscale || "").trim()) &&
    Boolean((cartella.data_nascita || "").trim());
  checklist.anagrafica = anagOk ? "ok" : "missing";
  checklist.familiari = "warn";
  checklist.contratto = "warn";
  const isDiurno = cartella.tipo_ospite === "diurno";
  const rettaOk = isDiurno
    ? Number(cartella.tariffa_giornaliera) > 0 || Number(cartella.tariffa_settimanale) > 0
    : Number(cartella.retta_mensile) > 0;
  checklist.retta = rettaOk ? "ok" : "missing";
  checklist.email_medico = "warn";
  let paiEnd = null;
  if (cartella.pai_scadenza && String(cartella.pai_scadenza).trim()) {
    const d = new Date(cartella.pai_scadenza);
    if (!Number.isNaN(d.getTime())) paiEnd = startOfDay(d);
  }
  if (!paiEnd) checklist.pai = "missing";
  else if (paiEnd < today) checklist.pai = "missing";
  else if ((paiEnd.getTime() - today.getTime()) / 86400000 <= 30) checklist.pai = "warn";
  else checklist.pai = "ok";

  const okCount = Object.values(checklist).filter((v) => v === "ok").length;
  const percent = Math.round((100 * okCount) / CHECKLIST_VOCI.length);
  const azioni = CHECKLIST_VOCI.map((v) => v.key)
    .filter((k) => checklist[k] !== "ok")
    .map((k) => CHECKLIST_AZIONI[k])
    .slice(0, 3);

  return {
    percent,
    checklist,
    azioni,
    tier: percent >= 85 ? "verde" : percent >= 60 ? "giallo" : "rosso",
  };
}

function completezzaVisuals(tier) {
  switch (tier) {
    case "verde":
      return {
        topBorder: "border-t-[4px] border-t-emerald-400",
        ring: "stroke-emerald-500",
        ringBg: "stroke-emerald-100",
        text: "text-emerald-600",
        box: "bg-emerald-50 border border-emerald-200 text-emerald-800",
        icon: "text-emerald-600",
      };
    case "giallo":
      return {
        topBorder: "border-t-[4px] border-t-amber-400",
        ring: "stroke-amber-500",
        ringBg: "stroke-amber-100",
        text: "text-amber-600",
        box: "bg-amber-50 border border-amber-200 text-amber-900",
        icon: "text-amber-600",
      };
    default:
      return {
        topBorder: "border-t-[4px] border-t-red-400",
        ring: "stroke-red-500",
        ringBg: "stroke-red-100",
        text: "text-red-600",
        box: "bg-red-50 border border-red-200 text-red-900",
        icon: "text-red-600",
      };
  }
}

function formatNomeLista(cognome, nome, nomeCompleto) {
  const toTitle = (s) =>
    String(s || "")
      .trim()
      .toLowerCase()
      .replace(/(^|\s|['’-])(\S)/g, (_, sep, ch) => `${sep}${ch.toUpperCase()}`);
  if (cognome && nome) return `${toTitle(cognome)} ${toTitle(nome)}`;
  return toTitle(nomeCompleto || "");
}

const SORT_OPTIONS = [
  { value: "completezza", label: "Completezza" },
  { value: "cognome", label: "Cognome" },
  { value: "data_ingresso", label: "Data ingresso" },
];

const SORT_DEFAULT_DIR = {
  completezza: "asc",
  cognome: "asc",
  data_ingresso: "desc",
};

function readStoredSort(key, allowed, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return allowed.includes(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
}

/** Data ingresso effettiva o, in pre-ingresso, quella prevista. */
function resolveDataIngresso(cartella) {
  return String(
    cartella?.data_ingresso || cartella?.data_ingresso_prevista || ""
  ).trim();
}

function compareCognomeNome(a, b) {
  const cmpCognome = (a.cognome || "").localeCompare(b.cognome || "", "it", {
    sensitivity: "base",
  });
  if (cmpCognome !== 0) return cmpCognome;
  return (a.nome || a.nome_completo || "").localeCompare(
    b.nome || b.nome_completo || "",
    "it",
    { sensitivity: "base" }
  );
}

function sortDirTitle(sortBy, sortDir) {
  if (sortBy === "data_ingresso") {
    return sortDir === "asc"
      ? "Dal più vecchio al più recente"
      : "Dal più recente al più vecchio";
  }
  if (sortBy === "cognome") {
    return sortDir === "asc" ? "A-Z" : "Z-A";
  }
  return sortDir === "asc"
    ? "Prima i fascicoli meno completi"
    : "Prima i fascicoli più completi";
}

function ListaSortHeader({ field, label, sortBy, sortDir, onSort, className }) {
  const active = sortBy === field;
  const Icon = sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={cn(
        "inline-flex items-center gap-1 text-left uppercase tracking-wide hover:text-slate-800",
        active ? "text-purple-700" : "text-slate-500",
        className
      )}
      title={
        active
          ? `Ordinato ${sortDirTitle(field, sortDir)}. Clicca per invertire.`
          : `Ordina per ${label.toLowerCase()}`
      }
    >
      {label}
      {active ? <Icon className="w-3 h-3 shrink-0" aria-hidden /> : null}
    </button>
  );
}

function resolveAggiornatoAt(cartella) {
  return (
    cartella?.aggiornato_at ||
    cartella?.updatedAt ||
    cartella?.data_ingresso ||
    ""
  );
}

function formatAggiornatoFa(iso) {
  if (!iso || !String(iso).trim()) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.max(0, Math.round((Date.now() - d.getTime()) / 86400000));
  if (days === 0) return "Agg. oggi";
  if (days === 1) return "Agg. ieri";
  return `Agg. ${days} giorni fa`;
}

function ospiteIniziali(cartella) {
  const c = String(cartella?.cognome || "").trim();
  const n = String(cartella?.nome || "").trim();
  if (c && n) return `${c[0]}${n[0]}`.toUpperCase();
  const parts = String(cartella?.nome_completo || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  if (parts[0]) return parts[0].slice(0, 2).toUpperCase();
  return "?";
}

const AVATAR_TONES = [
  "bg-violet-100 text-violet-700",
  "bg-sky-100 text-sky-700",
  "bg-rose-100 text-rose-700",
  "bg-amber-100 text-amber-800",
  "bg-emerald-100 text-emerald-700",
  "bg-fuchsia-100 text-fuchsia-700",
  "bg-indigo-100 text-indigo-700",
  "bg-teal-100 text-teal-700",
];

function avatarToneClass(cartella) {
  const key = String(cartella?.id || cartella?.cognome || cartella?.nome_completo || "");
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash + key.charCodeAt(i) * (i + 1)) % AVATAR_TONES.length;
  return AVATAR_TONES[hash];
}

function clinicalBadgeClass(tier) {
  switch (tier) {
    case "critica":
      return "bg-rose-100 text-rose-700 hover:bg-rose-100";
    case "da_aggiornare":
      return "bg-amber-100 text-amber-800 hover:bg-amber-100";
    case "in_valutazione":
      return "bg-sky-100 text-sky-800 hover:bg-sky-100";
    default:
      return "bg-emerald-50 text-emerald-700 hover:bg-emerald-50";
  }
}

function FascicoloProgressRing({ percent, tier, size = 64 }) {
  const safe = Math.min(100, Math.max(0, Number(percent) || 0));
  const cv = completezzaVisuals(tier);
  const stroke = 4;
  const r = (size - stroke) / 2 - 1;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (safe / 100) * circumference;

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      aria-label={`Completezza fascicolo ${safe}%`}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth={stroke}
          className={cv.ringBg}
        />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={cn(cv.ring, "transition-[stroke-dashoffset]")}
        />
      </svg>
      <span
        className={cn(
          "absolute inset-0 flex items-center justify-center text-[14px] font-bold tabular-nums leading-none",
          cv.text
        )}
      >
        {safe}%
      </span>
    </div>
  );
}

function ChecklistStatusIcon({ status }) {
  if (status === "ok") {
    return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" aria-hidden />;
  }
  if (status === "warn") {
    return <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" aria-hidden />;
  }
  return <XCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" aria-hidden />;
}

function ChecklistColumn({ items, checklist }) {
  return (
    <div className="space-y-1 min-w-0">
      {items.map(({ key, label }) => (
        <div key={key} className="flex items-center gap-1.5 min-w-0">
          <ChecklistStatusIcon status={checklist?.[key] || "missing"} />
          <span className="text-[11px] text-slate-600 truncate leading-none">{label}</span>
        </div>
      ))}
    </div>
  );
}

function FascicoloChecklistGrid({ checklist, showRetta = true }) {
  const left = showRetta
    ? CHECKLIST_LEFT
    : CHECKLIST_LEFT.filter((v) => v.key !== "retta");
  const right = showRetta
    ? CHECKLIST_RIGHT
    : CHECKLIST_RIGHT.filter((v) => v.key !== "retta");
  return (
    <div className="grid grid-cols-2 gap-x-4 mt-3">
      <ChecklistColumn items={left} checklist={checklist} />
      <ChecklistColumn items={right} checklist={checklist} />
    </div>
  );
}

/** Patologia in vista a blocchi: altezza fissa per allineare le card. */
function FascicoloPatologiaBox({ diagnosi }) {
  const text = (diagnosi || "").trim();

  return (
    <p
      className={cn(
        "mt-3 h-[2.6rem] shrink-0 text-[12px] leading-snug line-clamp-2 italic min-w-0",
        text ? "text-sky-700" : "text-slate-400"
      )}
      title={text || undefined}
    >
      {text || "Nessuna diagnosi indicata"}
    </p>
  );
}

const KPI_TONES = {
  violet: { card: "bg-violet-50", icon: "bg-violet-100 text-violet-600" },
  amber: { card: "bg-amber-50", icon: "bg-amber-100 text-amber-600" },
  rose: { card: "bg-rose-50", icon: "bg-rose-100 text-rose-600" },
  purple: { card: "bg-purple-50", icon: "bg-purple-100 text-purple-600" },
  sky: { card: "bg-sky-50", icon: "bg-sky-100 text-sky-600" },
  emerald: { card: "bg-emerald-50", icon: "bg-emerald-100 text-emerald-600" },
  slate: { card: "bg-slate-100", icon: "bg-white text-slate-500" },
};

function KpiStatCard({ icon: Icon, value, label, title, tone = "violet" }) {
  const t = KPI_TONES[tone] || KPI_TONES.violet;
  return (
    <div
      title={title}
      className={cn(
        "rounded-2xl px-3.5 py-3 min-h-[72px] flex flex-col justify-between shadow-sm border border-white/60",
        t.card
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className={cn("w-8 h-8 rounded-xl flex items-center justify-center shrink-0", t.icon)}>
          <Icon className="w-4 h-4" aria-hidden />
        </div>
        <p className="text-[22px] font-bold text-slate-900 tabular-nums leading-none">{value}</p>
      </div>
      <p className="text-[11px] text-slate-500 font-medium mt-2 leading-tight">{label}</p>
    </div>
  );
}

/** Avatar lista fascicoli: foto, altrimenti iniziali; click per caricare/sostituire. */
function OspiteListaAvatar({
  cartella,
  size = "md",
  ringClassName,
  canEdit,
  uploadingFotoId,
  onFotoClick,
}) {
  const boxClass =
    size === "lg" ? "w-12 h-12" :
    size === "md" ? "w-11 h-11" :
    size === "sm" ? "w-10 h-10" : "w-8 h-8";
  const iconClass =
    size === "lg" ? "w-5 h-5" :
    size === "md" ? "w-4 h-4" :
    size === "sm" ? "w-3.5 h-3.5" : "w-3 h-3";
  const initialsClass =
    size === "lg" ? "text-sm" :
    size === "md" ? "text-[13px]" :
    size === "sm" ? "text-[11px]" : "text-[10px]";
  const hasFoto = Boolean(cartella.foto_url);
  const uploading = uploadingFotoId === cartella.id;
  const initials = ospiteIniziali(cartella);

  const ring = cn(
    "rounded-full overflow-hidden flex items-center justify-center ring-1 relative",
    boxClass,
    ringClassName
  );

  const editOverlay = canEdit ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onFotoClick(e, cartella.id);
      }}
      disabled={uploadingFotoId !== null}
      className="absolute inset-0 flex items-center justify-center border-0 bg-black/0 hover:bg-black/35 transition-colors cursor-pointer group/foto rounded-full"
      title={hasFoto ? "Sostituisci foto ospite" : "Carica foto ospite"}
      data-testid={`btn-foto-lista-${cartella.id}`}
    >
      {uploading ? (
        <Loader2 className={cn(iconClass, "animate-spin text-white")} />
      ) : (
        <Camera className={cn(iconClass, "text-white opacity-0 group-hover/foto:opacity-100 transition-opacity")} aria-hidden />
      )}
    </button>
  ) : null;

  if (hasFoto) {
    return (
      <div className={cn("relative shrink-0", boxClass)}>
        <div className={cn(ring, "bg-white")}>
          <img
            src={cartella.foto_url}
            alt=""
            className="w-full h-full object-cover pointer-events-none"
          />
        </div>
        {editOverlay}
        {uploading && (
          <span className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-full">
            <Loader2 className={cn(iconClass, "animate-spin text-purple-700")} />
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={cn("relative shrink-0", boxClass)}>
      <div className={cn(ring, "font-semibold tracking-wide", avatarToneClass(cartella))}>
        <span className={initialsClass}>{initials}</span>
      </div>
      {editOverlay}
    </div>
  );
}

/** Anello percentuale documentazione (vista blocchi pre-ingresso). */
function PreIngressoDocRing({ percent, size = 46 }) {
  const safe = Math.min(100, Math.max(0, Number(percent) || 0));
  const stroke = 4;
  const r = (size - stroke) / 2 - 1;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (safe / 100) * circumference;

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      aria-label={`Documentazione completata al ${safe}%`}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth={stroke}
          className="stroke-slate-200"
        />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="stroke-amber-500 transition-[stroke-dashoffset]"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-slate-800 tabular-nums leading-none">
        {safe}%
      </span>
    </div>
  );
}

function OspiteStatoContextBox({ cartella, statoOspite, sv }) {
  if (statoOspite === STATO_PRE_INGRESSO) {
    const pct = Number(cartella.documentazione_percentuale) || 0;
    const ingresso = formatDataItShort(
      cartella.data_ingresso_prevista || cartella.data_ingresso
    );

    return (
      <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-center gap-3 min-w-0 text-[11px]">
        <Calendar className="w-3.5 h-3.5 text-amber-600 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <span className="font-medium text-amber-800">Ingresso {ingresso}</span>
          {(cartella.pre_ingresso_mancanze || []).length > 0 && (
            <p className="text-amber-900/80 truncate mt-0.5">
              • {(cartella.pre_ingresso_mancanze || []).slice(0, 2).join(" · ")}
            </p>
          )}
        </div>
        <PreIngressoDocRing percent={pct} size={44} />
      </div>
    );
  }
  if (statoOspite === STATO_DIMISSIONE_PROGRAMMATA) {
    const uscita = formatDataItShort(cartella.data_uscita);

    return (
      <div className="mt-3 rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 flex items-center gap-3 min-w-0 text-[11px]">
        <Calendar className="w-3.5 h-3.5 text-orange-600 shrink-0" aria-hidden />
        <div className="min-w-0">
          <span className="font-semibold text-orange-800">Uscita programmata: {uscita}</span>
        </div>
      </div>
    );
  }
  if (statoOspite === STATO_DIMESSO) {
    return (
      <div
        className={cn(
          "mt-3 rounded-lg border px-3 py-2 text-[11px] leading-snug flex gap-2 items-center",
          sv.stateBox
        )}
      >
        <CheckCircle2 className="w-3.5 h-3.5 shrink-0 opacity-80" />
        <span className="font-medium">
          Dimissione {formatDataItShort(cartella.data_uscita)}
        </span>
      </div>
    );
  }
  return null;
}

export default function FascicoliAssistenziali() {
  const { token, hasPermission, activeStruttura } = useAuth();
  const canDatiContabili = hasPermission("contabile:read");
  const navigate = useNavigate();
  const [cartelle, setCartelle] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatoClinico, setFilterStatoClinico] = useState("tutti");
  const [filterStatoOspite, setFilterStatoOspite] = useState("tutti");
  const [showArchiviate, setShowArchiviate] = useState(false);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("fascicoli_view") || "block"
  );
  const [sortBy, setSortBy] = useState(() =>
    readStoredSort("fascicoli_sort", ["completezza", "cognome", "data_ingresso"], "completezza")
  );
  const [sortDir, setSortDir] = useState(() =>
    readStoredSort("fascicoli_sort_dir", ["asc", "desc"], "asc")
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const handleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem("fascicoli_view", mode);
  };

  const persistSort = (nextBy, nextDir) => {
    localStorage.setItem("fascicoli_sort", nextBy);
    localStorage.setItem("fascicoli_sort_dir", nextDir);
  };

  const handleSortByChange = (nextBy) => {
    const nextDir = SORT_DEFAULT_DIR[nextBy] || "asc";
    setSortBy(nextBy);
    setSortDir(nextDir);
    setPage(1);
    persistSort(nextBy, nextDir);
  };

  const handleSortDirToggle = () => {
    const nextDir = sortDir === "asc" ? "desc" : "asc";
    setSortDir(nextDir);
    setPage(1);
    persistSort(sortBy, nextDir);
  };

  const handleSortHeader = (field) => {
    if (sortBy === field) {
      handleSortDirToggle();
      return;
    }
    handleSortByChange(field);
  };
  
  // Modal nuovo ospite
  const [showNuovoOspite, setShowNuovoOspite] = useState(false);
  const [creatingOspite, setCreatingOspite] = useState(false);
  const GIORNI_SETTIMANA = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
  const [nuovoOspite, setNuovoOspite] = useState({
    nome: "",
    cognome: "",
    sesso: "",
    room_id: "",
    tipo_ospite: "residente",
    giorni_settimana: [],
    orario_ingresso: "08:00",
    orario_uscita: "18:00",
    tariffa_giornaliera: "",
    tariffa_settimanale: "",
    retta_mensile: "",
  });
  const [camereDisponibili, setCamereDisponibili] = useState([]);
  const [loadingCamere, setLoadingCamere] = useState(false);

  const fotoListaInputRef = useRef(null);
  const [fotoListaCartellaId, setFotoListaCartellaId] = useState(null);
  const [uploadingFotoId, setUploadingFotoId] = useState(null);

  const [stampaInCorso, setStampaInCorso] = useState(false);
  const [showStampaDialog, setShowStampaDialog] = useState(false);
  const [showDimissioniRapide, setShowDimissioniRapide] = useState(false);
  const [showRicercaGlobale, setShowRicercaGlobale] = useState(false);
  const [confermaArchiviaTarget, setConfermaArchiviaTarget] = useState(null);
  const [archiviando, setArchiviando] = useState(false);
  const [campiStampa, setCampiStampa] = useState(createDefaultCampiStampa);
  const [decorrenzaSelection, setDecorrenzaSelection] = useState([]);
  const [showDecorrenzaDialog, setShowDecorrenzaDialog] = useState(false);
  const [decorrenzaMese, setDecorrenzaMese] = useState(() => new Date().toISOString().slice(0, 7));
  const [savingDecorrenza, setSavingDecorrenza] = useState(false);

  const [showComunicazioneMedico, setShowComunicazioneMedico] = useState(false);
  const [comunicazioneMedicoLoading, setComunicazioneMedicoLoading] = useState(false);
  const [comunicazioneMedicoInvio, setComunicazioneMedicoInvio] = useState(false);
  const [comunicazioneMedicoCartella, setComunicazioneMedicoCartella] = useState(null);
  const [comunicazioneMedicoSanitaria, setComunicazioneMedicoSanitaria] = useState(null);
  const [comunicazioneMedicoDocumenti, setComunicazioneMedicoDocumenti] = useState([]);
  const [comunicazioneOggetto, setComunicazioneOggetto] = useState("");
  const [comunicazioneMessaggio, setComunicazioneMessaggio] = useState("");
  const [comunicazioneAllegato, setComunicazioneAllegato] = useState("none");
  const [comunicazioneRegistraCopia, setComunicazioneRegistraCopia] = useState(true);

  const nuovoOspiteEmpty = {
    nome: "", cognome: "", sesso: "", room_id: "",
    tipo_ospite: "residente",
    giorni_settimana: [], orario_ingresso: "08:00", orario_uscita: "18:00",
    tariffa_giornaliera: "", tariffa_settimanale: "", retta_mensile: "",
  };

  // Funzione per creare nuovo ospite
  const handleCreaNuovoOspite = async () => {
    if (!nuovoOspite.nome || !nuovoOspite.cognome) {
      toast.error("Nome e cognome sono obbligatori");
      return;
    }
    if (!nuovoOspite.sesso || (nuovoOspite.sesso !== "M" && nuovoOspite.sesso !== "F")) {
      toast.error("Seleziona il sesso (maschio o femmina)");
      return;
    }
    
    setCreatingOspite(true);
    try {
      const isDiurno = nuovoOspite.tipo_ospite === "diurno";
      const payload = {
        nome: nuovoOspite.nome,
        cognome: nuovoOspite.cognome,
        sesso: nuovoOspite.sesso,
        tipo_ospite: nuovoOspite.tipo_ospite,
        createdFrom: "manual"
      };
      if (!isDiurno && nuovoOspite.room_id && nuovoOspite.room_id !== "none") {
        payload.room_id = nuovoOspite.room_id;
      }
      if (isDiurno) {
        payload.diurno = {
          giorni_settimana: nuovoOspite.giorni_settimana,
          orario_ingresso: nuovoOspite.orario_ingresso,
          orario_uscita: nuovoOspite.orario_uscita,
        };
        if (canDatiContabili) {
          if (nuovoOspite.tariffa_giornaliera !== "") payload.tariffa_giornaliera = parseFloat(nuovoOspite.tariffa_giornaliera) || 0;
          if (nuovoOspite.tariffa_settimanale !== "") payload.tariffa_settimanale = parseFloat(nuovoOspite.tariffa_settimanale) || 0;
          if (nuovoOspite.retta_mensile !== "") payload.retta_mensile = parseFloat(nuovoOspite.retta_mensile) || 0;
        }
      }
      const response = await axios.post(
        `${API_URL}/api/fascicoli-assistenziali`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newId = response.data.cartella_id || response.data.fascicolo_id;

      if (response.data.success && newId) {
        toast.success("Fascicolo assistenziale creato con successo");
        setShowNuovoOspite(false);
        setNuovoOspite(nuovoOspiteEmpty);
        // Naviga alla nuova fascicolo assistenziale
        navigate(`/fascicoli-assistenziali/${newId}`);
      } else {
        console.error("Risposta backend:", response.data);
        toast.error("Errore: cartella creata ma ID non ricevuto");
        // Ricarica la lista
        window.location.reload();
      }
    } catch (error) {
      console.error("Error creating cartella:", error);
      toast.error("Errore nella creazione della fascicolo assistenziale");
    } finally {
      setCreatingOspite(false);
    }
  };

  const refreshCartelle = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/fascicoli-assistenziali`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCartelle(response.data);
    } catch (error) {
      console.error("Error fetching fascicoli:", error);
    }
  }, [token]);

  const toggleDecorrenzaSelection = (id, checked) => {
    setDecorrenzaSelection((current) => checked ? [...new Set([...current, id])] : current.filter((item) => item !== id));
  };

  const handleAllineaDecorrenza = async () => {
    if (!decorrenzaSelection.length) return;
    setSavingDecorrenza(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/fascicoli-assistenziali/decorrenza-fatturazione/bulk`,
        { fascicolo_ids: decorrenzaSelection, mese: decorrenzaMese },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Decorrenza fatturazione aggiornata per ${data.modified} ospiti`);
      setDecorrenzaSelection([]);
      setShowDecorrenzaDialog(false);
      refreshCartelle();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile allineare la decorrenza");
    } finally {
      setSavingDecorrenza(false);
    }
  };

  useEffect(() => {
    const fetchFascicoli = async () => {
      try {
        await refreshCartelle();
      } finally {
        setLoading(false);
      }
    };

    fetchFascicoli();
  }, [refreshCartelle, activeStruttura?.id]);

  useEffect(() => {
    if (showNuovoOspite) {
      const fetchCamere = async () => {
        setLoadingCamere(true);
        try {
          const response = await axios.get(`${API_URL}/api/posti-liberi/camere-disponibili`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCamereDisponibili(response.data.camere || []);
        } catch (error) {
          console.error("Error fetching camere:", error);
        } finally {
          setLoadingCamere(false);
        }
      };
      fetchCamere();
    }
  }, [showNuovoOspite, token]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterStatoClinico, filterStatoOspite, showArchiviate]);

  const apriDettaglioOspite = (cartella) => {
    if (cartella?.id) navigate(`/fascicoli-assistenziali/${cartella.id}`);
  };

  const apriCaricaFoto = (e, cartellaId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!hasPermission("fascicolo:update")) {
      toast.error("Non hai permesso di modificare il fascicolo");
      return;
    }
    setFotoListaCartellaId(cartellaId);
    requestAnimationFrame(() => fotoListaInputRef.current?.click());
  };

  const handleFotoListaChange = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    const targetId = fotoListaCartellaId;
    setFotoListaCartellaId(null);
    if (!file || !targetId) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un file immagine");
      return;
    }
    setUploadingFotoId(targetId);
    try {
      const dataUrl = await resizeImageFileToJpegDataUrl(file, 720, 0.85);
      if (dataUrl.length > 2_500_000) {
        toast.error("Immagine troppo grande");
        return;
      }
      await axios.put(
        `${API_URL}/api/fascicoli-assistenziali/${targetId}`,
        { sezione: "anagrafica", dati: { foto_url: dataUrl } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCartelle((list) =>
        list.map((c) => (c.id === targetId ? { ...c, foto_url: dataUrl } : c))
      );
      toast.success("Foto aggiornata");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploadingFotoId(null);
    }
  };

  const apriConfermaArchivia = (e, cartella) => {
    e.preventDefault();
    e.stopPropagation();
    setConfermaArchiviaTarget({
      id: cartella.id,
      nome_completo: cartella.nome_completo,
    });
  };

  const handleArchiviaConfermata = async () => {
    if (!confermaArchiviaTarget) return;

    const { id: cartellaId, nome_completo: nomeOspite } = confermaArchiviaTarget;
    setArchiviando(true);
    try {
      await axios.patch(
        `${API_URL}/api/fascicoli-assistenziali/${cartellaId}`,
        { archiviata: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCartelle((list) =>
        list.map((c) => (c.id === cartellaId ? { ...c, archiviata: true } : c))
      );

      toast.success(`Cartella di ${nomeOspite} archiviata`);
      setConfermaArchiviaTarget(null);
    } catch (error) {
      console.error("Errore archiviazione:", error);
      toast.error("Errore nell'archiviazione");
    } finally {
      setArchiviando(false);
    }
  };

  // Funzione per ripristinare una cartella archiviata
  const handleRipristina = async (e, cartellaId, nomeOspite) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await axios.patch(`${API_URL}/api/fascicoli-assistenziali/${cartellaId}`, 
        { archiviata: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCartelle(cartelle.map(c => 
        c.id === cartellaId ? { ...c, archiviata: false } : c
      ));
      
      toast.success(`Cartella di ${nomeOspite} ripristinata`);
    } catch (error) {
      console.error("Errore ripristino:", error);
      toast.error("Errore nel ripristino");
    }
  };

  // Funzione per eliminare definitivamente
  const handleElimina = async (e, cartellaId, nomeOspite) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm(`Sei sicuro di voler eliminare definitivamente la cartella di ${nomeOspite}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/fascicoli-assistenziali/${cartellaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCartelle(cartelle.filter(c => c.id !== cartellaId));
      toast.success(`Cartella di ${nomeOspite} eliminata`);
    } catch (error) {
      console.error("Errore eliminazione:", error);
      toast.error("Errore nell'eliminazione");
    }
  };

  // Filtra le cartelle
  const cartelleFiltrate = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return cartelle
      .filter((c) => {
        const isArchiviata = c.archiviata === true;
        if (showArchiviate !== isArchiviata) return false;

        const clinical = computeClinicalStatus(c);
        const statoOspite = resolveStatoOspite(c);
        const matchStatoClinico =
          filterStatoClinico === "tutti" ||
          (filterStatoClinico === "critica" && clinical.tier === "critica") ||
          (filterStatoClinico === "da_aggiornare" && clinical.tier === "da_aggiornare") ||
          (filterStatoClinico === "in_valutazione" && clinical.tier === "in_valutazione") ||
          (filterStatoClinico === "ok" && clinical.tier === "ok");

        const matchStatoOspite =
          filterStatoOspite === "tutti" || filterStatoOspite === statoOspite;

        const medico = String(c.medico_curante || "").toLowerCase();
        const cf = String(c.codice_fiscale || "").toLowerCase();
        const uscitaLabel = formatDataUscitaBadge(c.data_uscita).toLowerCase();
        const uscitaRaw = String(c.data_uscita || "").toLowerCase();
        const matchSearch =
          !term ||
          (c.nome_completo || "").toLowerCase().includes(term) ||
          (c.nome || "").toLowerCase().includes(term) ||
          (c.cognome || "").toLowerCase().includes(term) ||
          (c.camera || "").toLowerCase().includes(term) ||
          (c.diagnosi_principale || "").toLowerCase().includes(term) ||
          medico.includes(term) ||
          uscitaLabel.includes(term) ||
          uscitaRaw.includes(term) ||
          cf.includes(term) ||
          matchOspiteStatoSearch(term, statoOspite);

        return matchSearch && matchStatoClinico && matchStatoOspite;
      })
      .sort((a, b) => {
        if (sortBy === "data_ingresso") {
          const da = resolveDataIngresso(a).slice(0, 10);
          const db = resolveDataIngresso(b).slice(0, 10);
          if (!da && !db) return compareCognomeNome(a, b);
          if (!da) return 1;
          if (!db) return -1;
          const byDate = da.localeCompare(db);
          if (byDate !== 0) return sortDir === "desc" ? -byDate : byDate;
          return compareCognomeNome(a, b);
        }
        let cmp =
          sortBy === "cognome"
            ? compareCognomeNome(a, b)
            : (() => {
                const pa = resolveCompletezza(a).percent;
                const pb = resolveCompletezza(b).percent;
                if (pa !== pb) return pa - pb;
                return compareCognomeNome(a, b);
              })();
        if (cmp === 0) return 0;
        return sortDir === "desc" ? -cmp : cmp;
      });
  }, [cartelle, searchTerm, filterStatoClinico, filterStatoOspite, showArchiviate, sortBy, sortDir]);

  const totalFiltered = cartelleFiltrate.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const cartellePagina = cartelleFiltrate.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const campiStampaSelezionati = useMemo(
    () =>
      STAMPA_LISTA_CAMPI.filter(
        (c) =>
          campiStampa[c.id] &&
          (canDatiContabili || c.id !== "retta_mensile")
      ).map((c) => c.id),
    [campiStampa, canDatiContabili]
  );

  const handleStampaLista = async () => {
    if (cartelleFiltrate.length === 0) {
      toast.error("Nessun fascicolo da stampare con i filtri attuali");
      return;
    }
    if (campiStampaSelezionati.length === 0) {
      toast.error("Seleziona almeno un campo da stampare");
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    let righe;
    try {
      setStampaInCorso(true);
      if (
        stampaRichiedeDettaglioAnagrafica(
          campiStampaSelezionati,
          cartelleFiltrate[0]
        )
      ) {
        const loadingId = toast.loading("Caricamento dati per la stampa…");
        try {
          const dettagli = await Promise.all(
            cartelleFiltrate.map((c) =>
              axios
                .get(`${API_URL}/api/fascicoli-assistenziali/${c.id}`, {
                  headers,
                })
                .then((res) => res.data || null)
                .catch(() => null)
            )
          );
          righe = cartelleFiltrate.map((c, i) =>
            buildRigaStampa(c, dettagli[i])
          );
        } finally {
          toast.dismiss(loadingId);
        }
      } else {
        righe = cartelleFiltrate.map((c) => buildRigaStampa(c, null));
      }
    } catch (e) {
      console.error("Stampa lista fascicoli:", e);
      toast.error("Errore nel caricamento dei dati per la stampa");
      return;
    } finally {
      setStampaInCorso(false);
    }

    const n = cartelleFiltrate.length;
    const html = generaHtmlStampaLista(righe, campiStampaSelezionati, n);
    if (!apriFinestraStampa(html)) {
      toast.error("Impossibile aprire la finestra di stampa. Controlla il blocco popup.");
      return;
    }
    setShowStampaDialog(false);
  };

  // Conta totali (solo non archiviati)
  const cartelleAttive = cartelle.filter((c) => !c.archiviata);
  const totaleCartelle = cartelleAttive.length;

  const kpiCompletezza = useMemo(() => {
    let fascicoliIncompleti = 0;
    let documentiMancanti = 0;
    let paiDaAggiornare = 0;
    let terapieDaVerificare = 0;
    let mediciSenzaEmail = 0;
    let retteNonConfigurate = 0;

    cartelleAttive.forEach((c) => {
      const comp = resolveCompletezza(c);
      if (comp.percent < 85) fascicoliIncompleti += 1;
      if (comp.checklist?.contratto === "missing") documentiMancanti += 1;
      if (comp.checklist?.pai !== "ok") paiDaAggiornare += 1;
      if ((Number(c.alert_attivi) || 0) > 0) terapieDaVerificare += 1;
      if (comp.checklist?.email_medico !== "ok") mediciSenzaEmail += 1;
      if (comp.checklist?.retta !== "ok") retteNonConfigurate += 1;
    });

    return {
      fascicoliIncompleti,
      documentiMancanti,
      paiDaAggiornare,
      terapieDaVerificare,
      mediciSenzaEmail,
      retteNonConfigurate,
    };
  }, [cartelleAttive]);

  const resetComunicazioneMedico = useCallback(() => {
    setComunicazioneMedicoCartella(null);
    setComunicazioneMedicoSanitaria(null);
    setComunicazioneMedicoDocumenti([]);
    setComunicazioneOggetto("");
    setComunicazioneMessaggio("");
    setComunicazioneAllegato("none");
    setComunicazioneRegistraCopia(true);
  }, []);

  const chiudiComunicazioneMedico = useCallback(() => {
    setShowComunicazioneMedico(false);
    resetComunicazioneMedico();
  }, [resetComunicazioneMedico]);

  const apriComunicazioneMedico = async (e, cartella) => {
    e.preventDefault();
    e.stopPropagation();
    const cartellaId = typeof cartella === "string" ? cartella : cartella?.id;
    const cartellaMeta =
      typeof cartella === "object" && cartella
        ? cartella
        : cartelle.find((c) => c.id === cartellaId);
    if (!cartellaId) return;

    setShowComunicazioneMedico(true);
    setComunicazioneMedicoLoading(true);
    setComunicazioneMedicoCartella(cartellaMeta || { id: cartellaId });
    setComunicazioneMedicoSanitaria(null);
    setComunicazioneMedicoDocumenti([]);
    setComunicazioneOggetto("");
    setComunicazioneMessaggio("");
    setComunicazioneAllegato("none");
    setComunicazioneRegistraCopia(true);

    try {
      const response = await axios.get(
        `${API_URL}/api/fascicoli-assistenziali/${cartellaId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data || {};
      const san = data.sanitaria || {};
      const anag = data.anagrafica || {};
      const nomeOspite = [anag.cognome, anag.nome].filter(Boolean).join(" ").trim()
        || cartellaMeta?.nome_completo
        || "Ospite";
      const nomeUpper = nomeOspite.toUpperCase();

      setComunicazioneMedicoSanitaria(san);
      const documentiAllegabili = [...(data.documenti || [])];
      const paiAttivo = data.pai_attivo;
      if (paiAttivo?.file_id && paiAttivo?.origine === "upload") {
        const giaPresente = documentiAllegabili.some(
          (d) => d.file_id === paiAttivo.file_id
        );
        if (!giaPresente) {
          documentiAllegabili.unshift({
            file_id: paiAttivo.file_id,
            sezione: "pai",
            file_name: paiAttivo.file_name || "PAI",
            descrizione: "PAI",
            file_ext: paiAttivo.file_ext,
          });
        }
      }
      setComunicazioneMedicoDocumenti(documentiAllegabili);
      setComunicazioneOggetto(`Comunicazione su ${nomeUpper}`);
      setComunicazioneMedicoCartella({
        ...(cartellaMeta || {}),
        id: cartellaId,
        nome_completo: nomeOspite,
      });
    } catch (err) {
      console.error("Errore caricamento dati comunicazione medico:", err);
      toast.error("Impossibile caricare i dati dell'ospite");
      chiudiComunicazioneMedico();
    } finally {
      setComunicazioneMedicoLoading(false);
    }
  };

  const medicoEmailComunicazione = useMemo(() => {
    const san = comunicazioneMedicoSanitaria || {};
    return (
      String(san.medico_curante_email_personale || "").trim()
      || String(san.medico_curante_email_blisteraggio || "").trim()
    );
  }, [comunicazioneMedicoSanitaria]);

  const medicoNomeComunicazione = useMemo(
    () => String(comunicazioneMedicoSanitaria?.medico_curante || "").trim(),
    [comunicazioneMedicoSanitaria]
  );

  const handleInviaComunicazioneMedico = async () => {
    if (!comunicazioneMedicoCartella?.id) return;
    if (!medicoEmailComunicazione) return;
    if (!comunicazioneMessaggio.trim()) {
      toast.error("Inserisci il messaggio");
      return;
    }
    if (!comunicazioneOggetto.trim()) {
      toast.error("Inserisci l'oggetto");
      return;
    }

    let allegatoPayload = null;
    if (comunicazioneAllegato && comunicazioneAllegato !== "none") {
      const doc = comunicazioneMedicoDocumenti.find(
        (d) => d.file_id === comunicazioneAllegato
      );
      if (doc) {
        allegatoPayload = {
          file_id: doc.file_id,
          sezione: doc.sezione,
          nome: doc.file_name || doc.descrizione || "Documento",
        };
      }
    }

    setComunicazioneMedicoInvio(true);
    try {
      await axios.post(
        `${API_URL}/api/comunicazioni/fascicolo/${comunicazioneMedicoCartella.id}/medico`,
        {
          oggetto: comunicazioneOggetto.trim(),
          messaggio: comunicazioneMessaggio.trim(),
          allegato: allegatoPayload,
          registra_copia: comunicazioneRegistraCopia,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Comunicazione inviata correttamente");
      chiudiComunicazioneMedico();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      const detailMsg = typeof detail === "string" ? detail : detail?.message;
      if (code === "email_medico_mancante") {
        toast.error("Nessuna email medico presente nei dati sanitari dell'ospite");
      } else if (detailMsg === "Impossibile allegare il documento selezionato") {
        toast.error("Impossibile allegare il documento selezionato");
      } else {
        toast.error("Errore durante l'invio della comunicazione");
      }
    } finally {
      setComunicazioneMedicoInvio(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-slate-200 rounded-xl w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 bg-slate-200 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-[100vw] box-border px-4 md:px-6 lg:px-8 py-3 md:py-4">
      <input
        type="file"
        ref={fotoListaInputRef}
        className="hidden"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFotoListaChange}
      />
      {/* Page Header - responsive */}
      <PageHeader
        title="Fascicoli Assistenziali"
        subtitle="Gestione documentazione assistenziale ospiti"
        icon={FolderOpen}
        iconContainerClassName="bg-[#F3EFFF] !shadow-none"
        iconClassName="text-violet-500"
      />

      {/* KPI — card pastello come da layout */}
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3 mb-4">
        <KpiStatCard
          icon={Users}
          tone="violet"
          value={totaleCartelle}
          label="Ospiti totali"
          title="Ospiti attivi in struttura"
        />
        <KpiStatCard
          icon={FileText}
          tone="amber"
          value={kpiCompletezza.fascicoliIncompleti}
          label="Fascicoli incompleti"
          title="Fascicoli con completezza inferiore all'85%"
        />
        <KpiStatCard
          icon={FileX}
          tone="rose"
          value={kpiCompletezza.documentiMancanti}
          label="Documenti mancanti"
          title="Ospiti senza contratto firmato"
        />
        <KpiStatCard
          icon={ClipboardList}
          tone="purple"
          value={kpiCompletezza.paiDaAggiornare}
          label="PAI da aggiornare"
          title="Ospiti con PAI assente, scaduto o in scadenza"
        />
        <KpiStatCard
          icon={Stethoscope}
          tone="sky"
          value={kpiCompletezza.terapieDaVerificare}
          label="Terapie da verificare"
          title="Ospiti con terapie o piani in scadenza (30 gg)"
        />
        <KpiStatCard
          icon={MessageCircle}
          tone="emerald"
          value={kpiCompletezza.mediciSenzaEmail}
          label="Medici senza email"
          title="Ospiti con medico curante senza email configurata"
        />
        {canDatiContabili && (
          <KpiStatCard
            icon={CreditCard}
            tone="slate"
            value={kpiCompletezza.retteNonConfigurate}
            label="Rette non configurate"
            title="Ospiti senza retta o tariffa configurata"
          />
        )}
        {hasPermission("fascicolo:create") ? (
          <button
            type="button"
            onClick={() => setShowNuovoOspite(true)}
            data-testid="btn-aggiungi-ospite"
            className="rounded-2xl min-h-[72px] px-3.5 py-3 flex flex-col items-start justify-between text-left text-white bg-[#B923F6] hover:bg-[#A21CAF] transition-colors"
          >
            <span className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
              <Plus className="w-4 h-4" aria-hidden />
            </span>
            <span className="text-sm font-semibold leading-tight">Aggiungi ospite</span>
          </button>
        ) : null}
      </div>

      {/* Ricerca e filtri — una riga */}
      <div className="rounded-2xl mb-4 bg-white px-3 py-2.5">
          <div className="flex flex-row flex-nowrap items-center gap-2 md:gap-2.5 min-h-9 overflow-x-auto overflow-y-visible [-webkit-overflow-scrolling:touch] pb-0.5 md:pb-0">
            <div className="relative flex-1 min-w-[min(100%,220px)] md:min-w-[240px] max-w-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none z-10" />
              <Input
                placeholder="Cerca per nome, camera, medico o stato…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-9 w-full bg-slate-50 border-slate-200/80 rounded-xl text-sm"
                data-testid="search-cartelle"
              />
            </div>

            <Select
              value={filterStatoOspite}
              onValueChange={(v) => {
                setFilterStatoOspite(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[132px] md:w-[148px] h-9 shrink-0 bg-slate-50 border-slate-200/80 rounded-xl text-sm">
                <SelectValue placeholder="Stato ospite" />
              </SelectTrigger>
              <SelectContent>
                {OSPITE_STATO_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filterStatoClinico}
              onValueChange={(v) => {
                setFilterStatoClinico(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[132px] md:w-[148px] h-9 shrink-0 bg-slate-50 border-slate-200/80 rounded-xl text-sm">
                <SelectValue placeholder="Priorità clinica" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutte le priorità</SelectItem>
                <SelectItem value="critica">Criticità</SelectItem>
                <SelectItem value="da_aggiornare">Da aggiornare</SelectItem>
                <SelectItem value="in_valutazione">In valutazione</SelectItem>
                <SelectItem value="ok">Tutto in regola</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex h-9 shrink-0 rounded-xl border border-slate-200/80 overflow-hidden bg-slate-50">
              <Select value={sortBy} onValueChange={handleSortByChange}>
                <SelectTrigger className="h-9 w-[138px] md:w-[156px] border-0 rounded-none shadow-none bg-transparent text-sm">
                  <SelectValue placeholder="Ordina" />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={handleSortDirToggle}
                className="flex items-center justify-center w-9 border-l border-slate-200 text-slate-500 hover:bg-white hover:text-purple-700"
                title={`Inverti ordinamento (${sortDirTitle(sortBy, sortDir)})`}
                aria-label={`Inverti ordinamento: ${sortDirTitle(sortBy, sortDir)}`}
              >
                {sortDir === "asc" ? (
                  <ArrowUp className="w-4 h-4" />
                ) : (
                  <ArrowDown className="w-4 h-4" />
                )}
              </button>
            </div>

            <Button
              type="button"
              variant={showArchiviate ? "default" : "outline"}
              className={cn(
                "h-9 shrink-0 px-2.5 rounded-xl border-slate-200 text-sm",
                showArchiviate ? "bg-slate-700 hover:bg-slate-800 border-slate-700 text-white" : "bg-slate-50"
              )}
              title={showArchiviate ? "Torna alle cartelle attive" : "Mostra cartelle archiviate"}
              onClick={() => setShowArchiviate(!showArchiviate)}
            >
              <Archive className={cn("w-4 h-4 sm:mr-2", showArchiviate ? "text-white" : "text-slate-600")} />
              <span className="hidden sm:inline whitespace-nowrap">
                {showArchiviate ? "Attive" : "Archivio"}
              </span>
              {cartelle.filter((c) => c.archiviata).length > 0 && (
                <Badge
                  className={cn(
                    "ml-1.5 sm:ml-2 text-[10px] px-1.5 min-w-[1.25rem] h-5 justify-center tabular-nums",
                    showArchiviate ? "bg-white/20 text-white hover:bg-white/20" : "bg-slate-500 text-white"
                  )}
                >
                  {cartelle.filter((c) => c.archiviata).length}
                </Badge>
              )}
            </Button>

            <RicercaOspiteGlobaleButton
              onClick={() => setShowRicercaGlobale(true)}
              className="h-9 rounded-xl bg-slate-50"
            />

            <Button
              type="button"
              variant="outline"
              className="h-9 shrink-0 px-2.5 rounded-xl border-slate-200 bg-slate-50 whitespace-nowrap text-sm"
              onClick={() => setShowStampaDialog(true)}
              disabled={cartelleFiltrate.length === 0 || stampaInCorso}
              data-testid="btn-stampa-lista-fascicoli"
              title="Stampa l'elenco visibile"
            >
              {stampaInCorso ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" />
              ) : (
                <Printer className="w-4 h-4 mr-2 shrink-0 text-slate-600" />
              )}
              Stampa
            </Button>

            {hasPermission("fascicolo:update") && (
            <Button
              type="button"
              variant="outline"
              className="h-9 shrink-0 px-2.5 rounded-xl border-rose-100 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:text-rose-800 whitespace-nowrap text-sm"
              onClick={() => setShowDimissioniRapide(true)}
              data-testid="btn-dimissioni-rapide"
              title="Imposta dimissione ospite"
            >
              <LogOut className="w-4 h-4 sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">Dimissioni</span>
            </Button>
            )}

            <div className="flex h-9 rounded-xl border border-slate-200/80 overflow-hidden shrink-0 bg-slate-50 ml-auto">
              <button
                type="button"
                onClick={() => handleViewMode("block")}
                className={`flex items-center justify-center w-10 transition-colors ${
                  viewMode === "block"
                    ? "bg-violet-500 text-white"
                    : "bg-transparent text-slate-500 hover:bg-white"
                }`}
                title="Vista a blocchi"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleViewMode("list")}
                className={`flex items-center justify-center w-10 transition-colors border-l border-slate-200/80 ${
                  viewMode === "list"
                    ? "bg-violet-500 text-white"
                    : "bg-transparent text-slate-500 hover:bg-white"
                }`}
                title="Vista a lista"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
      </div>

      <div className="rounded-2xl bg-[#E4EAF2] p-4 md:p-5">
      {/* Vista a blocchi */}
      {viewMode === "block" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {cartellePagina.map((cartella) => {
            const isDiurno = cartella.tipo_ospite === "diurno";
            const statoOspite = resolveStatoOspite(cartella);
            const sv = ospiteStatoVisuals(statoOspite);
            const completezza = resolveCompletezza(cartella);
            const clinical = computeClinicalStatus(cartella);
            const eta = computeEta(cartella.data_nascita);
            const nomeDisplay = formatNomeLista(
              cartella.cognome,
              cartella.nome,
              cartella.nome_completo
            );
            const camLabel =
              formatCameraAssegnata(cartella.camera) || cartella.camera || "Da assegnare";
            const aggiornatoLabel = formatAggiornatoFa(resolveAggiornatoAt(cartella));
            const medicoLabel = (cartella.medico_curante || "").trim() || "Non indicato";
            const isPreIngresso = statoOspite === STATO_PRE_INGRESSO;
            const ingressoLabel = formatDataItShort(resolveDataIngresso(cartella));
            const progressPercent =
              isPreIngresso && cartella.documentazione_percentuale != null
                ? cartella.documentazione_percentuale
                : completezza.percent;
            const progressTier =
              isPreIngresso && cartella.documentazione_percentuale != null
                ? cartella.documentazione_percentuale >= 85
                  ? "verde"
                  : cartella.documentazione_percentuale >= 60
                    ? "giallo"
                    : "rosso"
                : completezza.tier;

            return (
              <div
                key={cartella.id}
                role="button"
                tabIndex={0}
                data-testid={`cartella-${cartella.id}`}
                onClick={() => apriDettaglioOspite(cartella)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    apriDettaglioOspite(cartella);
                  }
                }}
                className="h-full min-h-0 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2"
              >
                <Card
                  className={cn(
                    "h-full flex flex-col overflow-hidden rounded-2xl shadow-sm hover:shadow-md transition-all cursor-pointer bg-white border border-slate-100/80",
                    sv.cardMuted,
                    isDiurno && "bg-amber-50/30"
                  )}
                >
                  <CardContent className="p-0 flex-1 flex flex-col min-h-0">
                    <div className="px-4 pt-4 pb-3 flex-1 flex flex-col min-h-0">

                      {/* Header: avatar · info · percentuale */}
                      <div className="flex items-start gap-3">
                        <OspiteListaAvatar
                          cartella={cartella}
                          size="md"
                          ringClassName="ring-white"
                          canEdit={hasPermission("fascicolo:update")}
                          uploadingFotoId={uploadingFotoId}
                          onFotoClick={apriCaricaFoto}
                        />

                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-slate-900 text-[15px] leading-snug line-clamp-1">
                            {nomeDisplay}
                          </h3>

                          <p className={cn(
                            "text-xs leading-snug mt-0.5 truncate",
                            isDiurno ? "text-amber-700" : "text-slate-500"
                          )}>
                            {isDiurno
                              ? `${cartella.orario_ingresso || "—"}–${cartella.orario_uscita || "—"}${eta != null ? ` • ${eta} anni` : ""}`
                              : `${camLabel}${eta != null ? ` • ${eta} anni` : ""}`}
                            {ingressoLabel !== "—" ? ` • ${ingressoLabel}` : ""}
                          </p>

                          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                            <Badge
                              className={cn(
                                "rounded-full px-2 py-0 text-[10px] font-semibold border-0 h-5 inline-flex shadow-none",
                                sv.badge
                              )}
                            >
                              {ospiteStatoLabel(statoOspite)}
                            </Badge>
                            {clinical.tier !== "ok" && (
                              <Badge
                                className={cn(
                                  "rounded-full px-2 py-0 text-[10px] font-semibold border-0 h-5 inline-flex shadow-none",
                                  clinicalBadgeClass(clinical.tier)
                                )}
                              >
                                {clinical.label}
                              </Badge>
                            )}
                            {isDiurno && (
                              <Badge className="rounded-full px-2 py-0 text-[10px] font-semibold border-0 h-5 inline-flex shadow-none bg-amber-100 text-amber-700 hover:bg-amber-100">
                                Diurno
                              </Badge>
                            )}
                          </div>
                        </div>

                        <FascicoloProgressRing
                          percent={progressPercent}
                          tier={progressTier}
                          size={56}
                        />
                      </div>

                      {/* Contenuto principale */}
                      {statoOspite === STATO_PRE_INGRESSO ||
                      statoOspite === STATO_DIMISSIONE_PROGRAMMATA ||
                      statoOspite === STATO_DIMESSO ? (
                        <OspiteStatoContextBox
                          cartella={cartella}
                          statoOspite={statoOspite}
                          sv={sv}
                        />
                      ) : (
                        <FascicoloChecklistGrid
                          checklist={completezza.checklist}
                          showRetta={canDatiContabili}
                        />
                      )}

                      <FascicoloPatologiaBox diagnosi={cartella.diagnosi_principale} />

                      {/* Footer */}
                      <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between gap-2 text-xs text-slate-500">
                        <p className="min-w-0 flex items-center gap-1 truncate">
                          <Stethoscope className="w-3.5 h-3.5 shrink-0 text-slate-400" aria-hidden />
                          <span className="truncate text-slate-600">{medicoLabel}</span>
                          {cartella.medico_curante ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0 text-slate-400 hover:text-purple-600"
                              title="Invia comunicazione riferita a questo ospite"
                              onClick={(e) => apriComunicazioneMedico(e, cartella)}
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                            </Button>
                          ) : null}
                        </p>
                        {aggiornatoLabel || (hasPermission("fascicolo:update") && !cartella.archiviata) ? (
                          <div
                            className="flex items-center gap-1 shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {aggiornatoLabel ? (
                              <span className="tabular-nums text-slate-400 text-[11px]">
                                {aggiornatoLabel}
                              </span>
                            ) : null}
                            {hasPermission("fascicolo:update") && !cartella.archiviata ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-slate-400 hover:text-slate-600"
                                title="Archivia"
                                data-testid={`btn-archivia-blocco-${cartella.id}`}
                                onClick={(e) => apriConfermaArchivia(e, cartella)}
                              >
                                <Archive className="w-3.5 h-3.5" />
                              </Button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      )}

      {/* Vista a lista */}
      {viewMode === "list" && (
        <div>
          {decorrenzaSelection.length > 0 && (
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 mb-3 rounded-2xl bg-amber-50 border border-amber-200">
              <span className="text-sm text-amber-900">{decorrenzaSelection.length} ospiti selezionati</span>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => setShowDecorrenzaDialog(true)}>
                Allinea decorrenza fatturazione
              </Button>
            </div>
          )}
          <div className="hidden md:grid md:grid-cols-[minmax(0,1fr)_7rem_minmax(0,1fr)_6.5rem_auto] gap-3 px-5 py-2 text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
            <ListaSortHeader
              field="cognome"
              label="Ospite / stato"
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={handleSortHeader}
            />
            <div>Camera</div>
            <div>Diagnosi · Medico</div>
            <ListaSortHeader
              field="data_ingresso"
              label="Ingresso"
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={handleSortHeader}
            />
            <div />
          </div>
          <ul className="space-y-2.5">
            {cartellePagina.map((cartella) => {
              const isDiurno = cartella.tipo_ospite === "diurno";
              const clinical = computeClinicalStatus(cartella);
              const statoOspite = resolveStatoOspite(cartella);
              const sv = ospiteStatoVisuals(statoOspite);
              const diagnosi = (cartella.diagnosi_principale || "").trim();
              return (
                <li key={cartella.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    data-testid={`cartella-list-${cartella.id}`}
                    onClick={() => apriDettaglioOspite(cartella)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        apriDettaglioOspite(cartella);
                      }
                    }}
                    className={cn(
                      "flex items-center gap-3 px-3 md:px-4 py-3.5 rounded-2xl bg-white border border-slate-100/80 shadow-sm hover:shadow-md transition-all cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-violet-400",
                      isDiurno && "bg-amber-50/40",
                      sv.cardMuted
                    )}
                  >
                    {hasPermission("fascicolo:update") && (
                      <Checkbox
                        checked={decorrenzaSelection.includes(cartella.id)}
                        onCheckedChange={(checked) => toggleDecorrenzaSelection(cartella.id, checked === true)}
                        onClick={(event) => event.stopPropagation()}
                        aria-label={`Seleziona ${cartella.nome_completo || cartella.id}`}
                      />
                    )}
                    <OspiteListaAvatar
                      cartella={cartella}
                      size="sm"
                      ringClassName="ring-white"
                      canEdit={hasPermission("fascicolo:update")}
                      uploadingFotoId={uploadingFotoId}
                      onFotoClick={apriCaricaFoto}
                    />

                    <div className="flex-1 min-w-0 md:grid md:grid-cols-[minmax(0,1fr)_7rem_minmax(0,1fr)_6.5rem] md:gap-3 md:items-center">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-slate-900 text-sm tracking-tight">
                            {cartella.cognome && cartella.nome ? (
                              <>
                                <span className="uppercase">{cartella.cognome}</span>{" "}
                                <span className="font-semibold text-slate-800">
                                  {cartella.nome}
                                </span>
                              </>
                            ) : (
                              cartella.nome_completo
                            )}
                          </span>
                          <Badge
                            className={cn(
                              "text-[10px] px-2 py-0 h-5 border-0 shadow-none rounded-full",
                              sv.badge
                            )}
                          >
                            {ospiteStatoLabel(statoOspite)}
                          </Badge>
                          {clinical.tier !== "ok" && (
                            <Badge
                              className={cn(
                                "text-[10px] px-2 py-0 h-5 border-0 shadow-none rounded-full",
                                clinicalBadgeClass(clinical.tier)
                              )}
                            >
                              {clinical.label}
                            </Badge>
                          )}
                          {isDiurno && (
                            <Badge className="text-[10px] px-2 py-0 h-5 border-0 shadow-none rounded-full bg-amber-100 text-amber-700 hover:bg-amber-100">
                              Diurno
                            </Badge>
                          )}
                          {cartella.pai_origine === "upload" && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-blue-300 text-blue-600">
                              PAI da file
                            </Badge>
                          )}
                          {cartella.pai_origine === "cartaceo" && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-purple-300 text-purple-600">
                              PAI cartaceo
                            </Badge>
                          )}
                        </div>
                        {isDiurno && cartella.orario_ingresso ? (
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            <span className="text-xs text-amber-700 flex items-center gap-0.5">
                              <Clock className="w-3 h-3" />
                              {cartella.orario_ingresso}–{cartella.orario_uscita}
                            </span>
                            {cartella.giorni_settimana?.map((g) => (
                              <span key={g} className="px-1 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded font-medium">{g}</span>
                            ))}
                          </div>
                        ) : (
                          <p className="md:hidden text-xs text-slate-500 mt-1 line-clamp-1">
                            {clinical.alertLine}
                          </p>
                        )}
                        <p className="md:hidden text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                          <Calendar className="w-3 h-3 shrink-0 text-slate-400" aria-hidden />
                          Ingresso {formatDataItShort(resolveDataIngresso(cartella))}
                        </p>
                      </div>

                      <div className="hidden md:block text-sm text-slate-600 truncate">
                        {isDiurno
                          ? (canDatiContabili && cartella.tariffa_giornaliera != null
                            ? `€ ${Number(cartella.tariffa_giornaliera).toLocaleString("it-IT")}/g`
                            : "Diurno")
                          : (formatCameraAssegnata(cartella.camera) || cartella.camera || "—")}
                      </div>

                      <div className="hidden md:flex flex-col min-w-0">
                        <span className={cn(
                          "text-xs line-clamp-1 italic",
                          diagnosi ? "text-sky-700 font-medium" : "text-slate-400"
                        )}>
                          {diagnosi || "Nessuna diagnosi"}
                        </span>
                        {cartella.medico_curante ? (
                          <span className="text-xs text-slate-400 flex items-center gap-1 mt-0.5 min-w-0">
                            <Stethoscope className="w-3 h-3 shrink-0" aria-hidden />
                            <span className="truncate">{cartella.medico_curante}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0 text-slate-400 hover:text-purple-600"
                              title="Invia comunicazione riferita a questo ospite"
                              onClick={(e) => apriComunicazioneMedico(e, cartella)}
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                            </Button>
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                            <span>Medico non indicato</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0 text-slate-400 hover:text-purple-600"
                              title="Invia comunicazione riferita a questo ospite"
                              onClick={(e) => apriComunicazioneMedico(e, cartella)}
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                            </Button>
                          </span>
                        )}
                      </div>

                      <div className="hidden md:block text-sm text-slate-500 tabular-nums">
                        {formatDataItShort(resolveDataIngresso(cartella))}
                      </div>
                    </div>

                    <div
                      className="flex items-center gap-0.5 flex-shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-purple-700"
                        title="Apri fascicolo"
                        onClick={() => navigate(`/fascicoli-assistenziali/${cartella.id}`)}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      {hasPermission("fascicolo:update") && (!cartella.archiviata ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400"
                          onClick={(e) => apriConfermaArchivia(e, cartella)}
                          title="Archivia"
                          data-testid={`btn-archivia-lista-${cartella.id}`}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-emerald-600"
                            onClick={(e) =>
                              handleRipristina(e, cartella.id, cartella.nome_completo)
                            }
                            title="Ripristina"
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                          {hasPermission("fascicolo:delete") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500"
                            onClick={(e) => handleElimina(e, cartella.id, cartella.nome_completo)}
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          )}
                        </>
                      ))}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {cartelleFiltrate.length === 0 && (
        <div className="text-center py-12">
          <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nessun fascicolo assistenziale trovato</p>
        </div>
      )}
      </div>

      {cartelleFiltrate.length > 0 && (
        <div className="flex flex-col lg:flex-row items-center justify-between gap-4 mt-8 pt-5 border-t border-slate-200/90">
          <div className="flex items-center gap-2 text-sm text-slate-600 flex-wrap justify-center">
            <span className="text-slate-500">Mostra</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9 w-[76px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="12">12</SelectItem>
                <SelectItem value="24">24</SelectItem>
                <SelectItem value="48">48</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-slate-500">elementi per pagina</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Indietro
            </Button>
            <span className="text-sm text-slate-600 tabular-nums px-2">
              Pagina {safePage} / {totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Avanti
            </Button>
          </div>
          <p className="text-sm text-slate-500 tabular-nums">
            {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, totalFiltered)} di{" "}
            {totalFiltered}
          </p>
        </div>
      )}

      <RicercaOspiteGlobaleDialog
        open={showRicercaGlobale}
        onOpenChange={setShowRicercaGlobale}
      />

      <DimissioniRapideDialog
        open={showDimissioniRapide}
        onOpenChange={setShowDimissioniRapide}
        onSuccess={refreshCartelle}
      />

      <Dialog open={showDecorrenzaDialog} onOpenChange={setShowDecorrenzaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Allinea decorrenza fatturazione</DialogTitle>
            <DialogDescription>
              L&apos;operazione usa il primo giorno del mese scelto per il calcolo della retta. Non modifica la data di ingresso reale né i dati Alloggiati Web.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Mese di decorrenza</Label>
            <Input type="month" value={decorrenzaMese} onChange={(event) => setDecorrenzaMese(event.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDecorrenzaDialog(false)} disabled={savingDecorrenza}>Annulla</Button>
            <Button onClick={handleAllineaDecorrenza} disabled={savingDecorrenza} className="bg-amber-600 hover:bg-amber-700">
              {savingDecorrenza ? "Aggiornamento…" : "Conferma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confermaArchiviaTarget}
        onOpenChange={(open) => {
          if (!open && !archiviando) setConfermaArchiviaTarget(null);
        }}
      >
        <AlertDialogContent data-testid="dialog-conferma-archivia">
          <AlertDialogHeader>
            <AlertDialogTitle>Archivia fascicolo</AlertDialogTitle>
            <AlertDialogDescription>
              Confermi l&apos;archiviazione del fascicolo di{" "}
              <span className="font-medium text-slate-700">
                {confermaArchiviaTarget?.nome_completo}
              </span>
              ? Il fascicolo resterà consultabile dall&apos;archivio e non verrà eliminato.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiviando}>Annulla</AlertDialogCancel>
            <Button
              className="bg-slate-700 hover:bg-slate-800"
              onClick={handleArchiviaConfermata}
              disabled={archiviando}
            >
              {archiviando ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Archivia
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modale invio comunicazione al medico */}
      <Dialog
        open={showComunicazioneMedico}
        onOpenChange={(open) => {
          if (!open && !comunicazioneMedicoInvio) chiudiComunicazioneMedico();
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-comunicazione-medico">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-purple-600" />
              Invia comunicazione
            </DialogTitle>
            {comunicazioneMedicoCartella?.nome_completo && (
              <DialogDescription>
                Comunicazione riferita all&apos;ospite{" "}
                <span className="font-medium text-slate-700">
                  {comunicazioneMedicoCartella.nome_completo}
                </span>
              </DialogDescription>
            )}
          </DialogHeader>

          {comunicazioneMedicoLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Caricamento dati…
            </div>
          ) : (
            <div className="space-y-4 py-1">
              <div>
                <Label className="text-xs text-slate-500">Destinatario</Label>
                <div className="mt-1.5 rounded-xl border border-slate-200 bg-slate-50/80 p-3 flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                    <Stethoscope className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900">
                      {medicoNomeComunicazione || "Medico non indicato"}
                    </p>
                    {medicoEmailComunicazione ? (
                      <p className="text-sm text-slate-600 break-all">{medicoEmailComunicazione}</p>
                    ) : null}
                    <p className="text-[11px] text-slate-400 mt-1">
                      Dott. / Referente collegato (dai dati sanitari)
                    </p>
                  </div>
                </div>
                {!medicoEmailComunicazione && (
                  <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    Nessuna email medico presente nei dati sanitari dell&apos;ospite.{" "}
                    <button
                      type="button"
                      className="font-medium text-purple-700 underline hover:text-purple-900"
                      onClick={() => {
                        const fid = comunicazioneMedicoCartella?.id;
                        chiudiComunicazioneMedico();
                        if (fid) navigate(`/fascicoli-assistenziali/${fid}?tab=sanitaria`);
                      }}
                    >
                      Aggiungi email medico
                    </button>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="comunicazione-oggetto" className="text-xs text-slate-500">
                  Oggetto
                </Label>
                <Input
                  id="comunicazione-oggetto"
                  value={comunicazioneOggetto}
                  onChange={(e) => setComunicazioneOggetto(e.target.value)}
                  className="mt-1.5"
                  disabled={comunicazioneMedicoInvio}
                />
              </div>

              <div>
                <Label htmlFor="comunicazione-messaggio" className="text-xs text-slate-500">
                  Messaggio
                </Label>
                <Textarea
                  id="comunicazione-messaggio"
                  value={comunicazioneMessaggio}
                  onChange={(e) => setComunicazioneMessaggio(e.target.value)}
                  placeholder="Scrivi qui il messaggio…"
                  rows={5}
                  className="mt-1.5 resize-y min-h-[120px]"
                  disabled={comunicazioneMedicoInvio}
                />
              </div>

              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1.5">
                  <Paperclip className="w-3.5 h-3.5" />
                  Allega documento dal fascicolo
                </Label>
                <Select
                  value={comunicazioneAllegato}
                  onValueChange={setComunicazioneAllegato}
                  disabled={comunicazioneMedicoInvio}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Seleziona documento da allegare" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun allegato</SelectItem>
                    {comunicazioneMedicoDocumenti.map((doc) => (
                      <SelectItem key={doc.file_id} value={doc.file_id}>
                        {doc.file_name || doc.descrizione || "Documento"}
                        {doc.sezione ? ` (${doc.sezione})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-start gap-2">
                <Checkbox
                  id="comunicazione-registra-copia"
                  checked={comunicazioneRegistraCopia}
                  onCheckedChange={(v) => setComunicazioneRegistraCopia(v === true)}
                  disabled={comunicazioneMedicoInvio}
                />
                <Label
                  htmlFor="comunicazione-registra-copia"
                  className="text-sm font-normal leading-snug cursor-pointer"
                >
                  Registra copia della comunicazione nel fascicolo dell&apos;ospite
                </Label>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={chiudiComunicazioneMedico}
              disabled={comunicazioneMedicoInvio}
            >
              Annulla
            </Button>
            <Button
              type="button"
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleInviaComunicazioneMedico}
              disabled={
                comunicazioneMedicoLoading
                || comunicazioneMedicoInvio
                || !medicoEmailComunicazione
                || !comunicazioneMessaggio.trim()
              }
              data-testid="btn-invia-comunicazione-medico"
            >
              {comunicazioneMedicoInvio ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog selezione campi stampa */}
      <Dialog
        open={showStampaDialog}
        onOpenChange={(v) => {
          if (!stampaInCorso) setShowStampaDialog(v);
        }}
      >
        <DialogContent className="max-w-lg" data-testid="dialog-stampa-lista-fascicoli">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-slate-600" />
              Stampa elenco ospiti
            </DialogTitle>
            <DialogDescription>
              Scegli i campi da includere nella stampa dell&apos;elenco filtrato (
              {cartelleFiltrate.length}{" "}
              {cartelleFiltrate.length === 1 ? "ospite" : "ospiti"}).
              I campi anagrafici aggiuntivi richiedono il caricamento del dettaglio di ogni fascicolo.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2 max-h-[min(60vh,420px)] overflow-y-auto">
            {STAMPA_LISTA_CAMPI.filter(
              (campo) => canDatiContabili || campo.id !== "retta_mensile"
            ).map((campo) => {
              const inputId = `stampa-campo-${campo.id}`;
              return (
                <div key={campo.id} className="flex items-center gap-2">
                  <Checkbox
                    id={inputId}
                    checked={!!campiStampa[campo.id]}
                    onCheckedChange={(checked) =>
                      setCampiStampa((prev) => ({
                        ...prev,
                        [campo.id]: checked === true,
                      }))
                    }
                    data-testid={`checkbox-stampa-${campo.id}`}
                  />
                  <Label htmlFor={inputId} className="text-sm font-normal cursor-pointer">
                    {campo.label}
                  </Label>
                </div>
              );
            })}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCampiStampa(createDefaultCampiStampa())}
              disabled={stampaInCorso}
            >
              Predefiniti
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowStampaDialog(false)}
              disabled={stampaInCorso}
            >
              Annulla
            </Button>
            <Button
              type="button"
              onClick={handleStampaLista}
              disabled={
                cartelleFiltrate.length === 0 ||
                stampaInCorso ||
                campiStampaSelezionati.length === 0
              }
              data-testid="btn-conferma-stampa-lista-fascicoli"
            >
              {stampaInCorso ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Printer className="w-4 h-4 mr-2" />
              )}
              Stampa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Nuovo Ospite */}
      <Dialog open={showNuovoOspite} onOpenChange={(v) => { setShowNuovoOspite(v); if (!v) setNuovoOspite(nuovoOspiteEmpty); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <UserPlus className="w-6 h-6 text-purple-500" />
              Nuovo Ospite
            </DialogTitle>
            <p className="text-sm text-slate-500 mt-1">
              Crea un nuovo fascicolo assistenziale per un ospite
            </p>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Tipo ospite */}
            <div className="space-y-2">
              <Label>Tipo ospite *</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setNuovoOspite({ ...nuovoOspite, tipo_ospite: "residente" })}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-2.5 text-sm font-medium transition-colors",
                    nuovoOspite.tipo_ospite === "residente"
                      ? "border-purple-500 bg-purple-50 text-purple-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  )}
                >
                  <Bed className="w-4 h-4" />
                  Residente
                </button>
                <button
                  type="button"
                  onClick={() => setNuovoOspite({ ...nuovoOspite, tipo_ospite: "diurno" })}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-2.5 text-sm font-medium transition-colors",
                    nuovoOspite.tipo_ospite === "diurno"
                      ? "border-amber-500 bg-amber-50 text-amber-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  )}
                >
                  <Sun className="w-4 h-4" />
                  Diurno
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={nuovoOspite.nome}
                  onChange={(e) => setNuovoOspite({...nuovoOspite, nome: e.target.value})}
                  placeholder="Mario"
                  data-testid="input-nuovo-ospite-nome"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cognome">Cognome *</Label>
                <Input
                  id="cognome"
                  value={nuovoOspite.cognome}
                  onChange={(e) => setNuovoOspite({...nuovoOspite, cognome: e.target.value})}
                  placeholder="Rossi"
                  data-testid="input-nuovo-ospite-cognome"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nuovo-ospite-sesso">Sesso *</Label>
              <Select
                value={nuovoOspite.sesso || undefined}
                onValueChange={(v) => setNuovoOspite({ ...nuovoOspite, sesso: v })}
              >
                <SelectTrigger id="nuovo-ospite-sesso" data-testid="select-nuovo-ospite-sesso">
                  <SelectValue placeholder="Seleziona maschio o femmina" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Maschio</SelectItem>
                  <SelectItem value="F">Femmina</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Camera: solo per residenti */}
            {nuovoOspite.tipo_ospite === "residente" && (
              <div className="space-y-2">
                <Label htmlFor="camera">Camera (opzionale)</Label>
                {loadingCamere ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Caricamento camere...
                  </div>
                ) : (
                  <Select
                    value={nuovoOspite.room_id || "none"}
                    onValueChange={(v) => setNuovoOspite({...nuovoOspite, room_id: v})}
                  >
                    <SelectTrigger data-testid="input-nuovo-ospite-camera">
                      <SelectValue placeholder="Da assegnare" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Da assegnare</SelectItem>
                      {camereDisponibili.map((camera) => (
                        <SelectItem key={camera.id} value={camera.id}>
                          {camera.name} - {camera.type} ({camera.posti_liberi} {camera.posti_liberi === 1 ? "posto libero" : "posti liberi"})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {camereDisponibili.length === 0 && !loadingCamere && (
                  <p className="text-xs text-amber-600">Nessuna camera disponibile. Puoi assegnarla dopo dalla modifica ospite.</p>
                )}
              </div>
            )}

            {/* Campi diurno */}
            {nuovoOspite.tipo_ospite === "diurno" && (
              <div className="rounded-lg border-2 border-amber-200 bg-amber-50/40 p-4 space-y-4">
                <p className="text-sm font-medium text-amber-800 flex items-center gap-1.5">
                  <Sun className="w-4 h-4" />
                  Presenza diurna
                </p>

                <div className="space-y-2">
                  <Label className="text-xs text-slate-600">Giorni di presenza</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {GIORNI_SETTIMANA.map((g) => {
                      const selected = nuovoOspite.giorni_settimana.includes(g);
                      return (
                        <button
                          key={g}
                          type="button"
                          onClick={() =>
                            setNuovoOspite({
                              ...nuovoOspite,
                              giorni_settimana: selected
                                ? nuovoOspite.giorni_settimana.filter((d) => d !== g)
                                : [...nuovoOspite.giorni_settimana, g],
                            })
                          }
                          className={cn(
                            "px-2.5 py-1 rounded text-xs font-medium border transition-colors",
                            selected
                              ? "bg-amber-500 text-white border-amber-500"
                              : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"
                          )}
                        >
                          {g}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Orario ingresso
                    </Label>
                    <Input
                      type="time"
                      value={nuovoOspite.orario_ingresso}
                      onChange={(e) => setNuovoOspite({ ...nuovoOspite, orario_ingresso: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Orario uscita
                    </Label>
                    <Input
                      type="time"
                      value={nuovoOspite.orario_uscita}
                      onChange={(e) => setNuovoOspite({ ...nuovoOspite, orario_uscita: e.target.value })}
                    />
                  </div>
                </div>

                {canDatiContabili && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-600">Tariffa/giorno (€)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0,00"
                        value={nuovoOspite.tariffa_giornaliera}
                        onChange={(e) => setNuovoOspite({ ...nuovoOspite, tariffa_giornaliera: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-600">Tariffa/sett. (€)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0,00"
                        value={nuovoOspite.tariffa_settimanale}
                        onChange={(e) => setNuovoOspite({ ...nuovoOspite, tariffa_settimanale: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-600">Retta mensile (€)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0,00"
                        value={nuovoOspite.retta_mensile}
                        onChange={(e) => setNuovoOspite({ ...nuovoOspite, retta_mensile: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className={cn("p-3 rounded-lg text-sm", nuovoOspite.tipo_ospite === "diurno" ? "bg-amber-50 text-amber-800" : "bg-purple-50 text-purple-700")}>
              <p>
                Dopo la creazione potrai completare l'anagrafica e tutti i dati clinici dalla scheda dell'ospite.
              </p>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => { setShowNuovoOspite(false); setNuovoOspite(nuovoOspiteEmpty); }}
              disabled={creatingOspite}
            >
              Annulla
            </Button>
            <Button
              onClick={handleCreaNuovoOspite}
              disabled={
                creatingOspite ||
                !nuovoOspite.nome ||
                !nuovoOspite.cognome ||
                !nuovoOspite.sesso
              }
              className={nuovoOspite.tipo_ospite === "diurno" ? "bg-amber-500 hover:bg-amber-600" : "bg-purple-600 hover:bg-purple-700"}
              data-testid="btn-conferma-nuovo-ospite"
            >
              {creatingOspite ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creazione...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Crea Fascicolo
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
